import { AlertCircle, AlertTriangle, CheckCircle, CreditCard, Download, ExternalLink, Receipt, XCircle } from "lucide-react";

export interface BillingInvoice {
  id: number;
  paid_at: string | null;
  amount_cents: number;
  status: string;
  invoice_pdf_url: string | null;
  hosted_invoice_url: string | null;
}

export interface BillingStatus {
  subscribed: boolean;
  status: string | null;
  payments_count: number;
  total_paid_cents: number;
  payment_failed: boolean;
  invoices: BillingInvoice[];
}

interface Props {
  billing: BillingStatus;
}

const TOTAL_PAYMENTS = 25;
const MONTHLY_CENTS  = 80000; // $800

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

async function redirectToStripe(endpoint: string) {
  const res = await fetch(endpoint, {
    method:  "POST",
    headers: { "X-CSRF-Token": getCsrfToken(), "Content-Type": "application/json" },
  });
  const data = await res.json();
  if (data.url) window.location.href = data.url;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDollars(cents: number) {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
}

export default function BillingSection({ billing }: Props) {
  const remainingCents = Math.max(0, TOTAL_PAYMENTS * MONTHLY_CENTS - billing.total_paid_cents);
  const progressPct    = Math.min(100, (billing.payments_count / TOTAL_PAYMENTS) * 100);

  if (!billing.subscribed) {
    return (
      <div className="px-5 py-6 flex flex-col items-start gap-3">
        <p className="text-sm text-gray-600">
          Set up your $800/month subscription to activate Docket. You'll be redirected to Stripe's
          secure checkout — no card info touches this server.
        </p>
        <button
          onClick={() => redirectToStripe("/api/billing/checkout")}
          className="flex items-center gap-2 text-sm font-medium text-white bg-green-700 hover:bg-green-800 rounded-md px-4 py-2 transition-colors"
        >
          <CreditCard size={14} /> Set Up Billing
        </button>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {/* Payment failed banner */}
      {billing.payment_failed && (
        <div className="px-5 py-3 flex items-center gap-2 bg-red-50 text-red-700 text-sm">
          <AlertCircle size={14} className="flex-shrink-0" />
          <span>Your last payment failed. Please update your payment method.</span>
          <button
            onClick={() => redirectToStripe("/api/billing/portal")}
            className="ml-auto text-xs font-medium underline hover:no-underline"
          >
            Fix now
          </button>
        </div>
      )}

      {/* Summary */}
      <div className="px-5 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {billing.status === "past_due" ? (
              <AlertTriangle size={14} className="text-amber-500" />
            ) : billing.status === "cancelled" ? (
              <XCircle size={14} className="text-red-500" />
            ) : (
              <CheckCircle size={14} className="text-green-600" />
            )}
            <span className="text-sm font-medium text-gray-900 capitalize">
              {billing.status ?? "Active"}
            </span>
          </div>
          <button
            onClick={() => redirectToStripe("/api/billing/portal")}
            className="text-xs font-medium text-gray-500 hover:text-gray-900 border border-gray-200 rounded-md px-3 py-1.5 hover:border-gray-300 transition-colors"
          >
            Manage Billing
          </button>
        </div>

        {/* Progress */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-gray-500">
            <span>{billing.payments_count} of {TOTAL_PAYMENTS} payments</span>
            <span>{formatDollars(billing.total_paid_cents)} of $20,000</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-600 rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="text-xs text-gray-400 text-right">{formatDollars(remainingCents)} remaining</div>
        </div>
      </div>

      {/* Invoice history */}
      {billing.invoices.length > 0 && (
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Receipt size={13} className="text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Invoice History</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th className="text-left pb-2 font-medium">Date</th>
                <th className="text-left pb-2 font-medium">Amount</th>
                <th className="text-left pb-2 font-medium">Status</th>
                <th className="text-right pb-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {billing.invoices.map((inv) => (
                <tr key={inv.id} className="text-gray-700">
                  <td className="py-2 text-xs">{formatDate(inv.paid_at)}</td>
                  <td className="py-2 text-xs">{formatDollars(inv.amount_cents)}</td>
                  <td className="py-2 text-xs">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                      inv.status === "paid"
                        ? "bg-green-50 text-green-700"
                        : inv.status === "failed"
                        ? "bg-red-50 text-red-700"
                        : "bg-gray-100 text-gray-600"
                    }`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {inv.hosted_invoice_url && (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900"
                        >
                          <ExternalLink size={11} /> View
                        </a>
                      )}
                      {inv.invoice_pdf_url && (
                        <a
                          href={inv.invoice_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900"
                        >
                          <Download size={11} /> PDF
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
